<script lang="ts" setup>
import { computed, defineProps, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import EmailIcon from "@/components/icons/EmailIcon.vue";
import InstagramIcon from "@/components/icons/InstagramIcon.vue";
import BlogButton from "@/components/buttons/BlogButton.vue";
import LoginButton from "@/components/buttons/LoginButton.vue";
import { Breadcrumbs } from "@/types/breadcrumbs";
import router from "@/router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { useSocialsStore } from "@/stores/socialsStore";

const { t } = useI18n();
const socialStore = useSocialsStore();

const props = defineProps<{
  showBlogBtn: boolean;
  breadcrumbs?: Breadcrumbs[];
}>();

const emailLink = computed(() => socialStore.socialLinks.email);
const instagramLink = computed(() => socialStore.socialLinks.instagram);

onMounted(async () => {
  await socialStore.loadSocials();
});

function openSocialLink(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function openLoginPage() {
  router.push({ name: ROUTE_NAMES.USER });
}
</script>

<template>
  <header
    class="absolute top-0 left-0 right-0 flex justify-between items-center p-0 bg-[#311C1333] backdrop-blur-[20px] border-b-[1px] border-white border-opacity-30 z-50 h-12 xl:h-[65px]"
  >
    <div class="inline-flex items-center">
      <BlogButton v-if="props.showBlogBtn" />

      <!-- BREADCRUMBS -->
      <nav
        class="flex justify-between p-0 ml-[17px] md:ml-[25px] xl:ml-8"
        v-if="props.breadcrumbs && props.breadcrumbs.length"
      >
        <ol
          class="inline-flex items-center space-x-3 [&_.active-breadcrumb]:opacity-70"
        >
          <template
            v-for="(breadcrumb, index) in props.breadcrumbs"
            :key="breadcrumb.path"
          >
            <li>
              <span
                v-if="index === props.breadcrumbs.length - 1"
                class="hidden xl:inline-flex items-center py-1 sf-pro-sm text-white active-breadcrumb cursor-default"
              >
                {{ t(breadcrumb.label) }}
              </span>
              <RouterLink
                v-else
                :to="breadcrumb.path"
                class="inline-flex items-center py-1 focus:outline-none sf-pro-sm text-white"
              >
                <svg
                  v-if="index === 0"
                  width="7"
                  height="13"
                  viewBox="0 0 7 13"
                  fill="none"
                  class="mr-2 xl:hidden"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.5 1L1 6.5L6.5 12"
                    stroke="white"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                {{ t(breadcrumb.label) }}
              </RouterLink>
            </li>

            <span
              v-if="index < props.breadcrumbs.length - 1"
              class="mx-2 sf-pro-sm text-white opacity-70 hidden xl:block"
            >
              /
            </span>
          </template>
        </ol>
      </nav>
    </div>

    <!-- RIGHT BUTTONS -->
    <div class="xl:flex gap-4 mr-2 hidden">
      <button class="group rounded-full" v-if="emailLink">
        <EmailIcon type="white" />
      </button>
      <button
        class="group rounded-full"
        v-if="instagramLink"
        @click="openSocialLink(instagramLink)"
      >
        <InstagramIcon type="white" />
      </button>
      <LoginButton @click="openLoginPage" />
    </div>
  </header>
</template>
